import { useMutation } from '@apollo/client';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { DeletePersonMfa, MfaType } from 'Apollo/mutations/person/DeletePersonMfa';
import CurrentPerson from 'Context/CurrentPerson';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';
import { ErrorHandler } from 'Theme/components/ErrorHandler';

interface DeletePersonMfaContentProps {
  targetImmutableId: string;
  type: MfaType;
  onError: (error: any) => void;
}

function DeletePersonMfaContent({
  targetImmutableId,
  type,
  onError: handleError
}: Readonly<DeletePersonMfaContentProps>) {
  const { onSuccess, onError } = useNotification();
  const history = useHistory();
  const ts = useTranslate();

  const onCompleted = () => {
    onSuccess('page.deletePersonMfa.success');
    history.goBack();
  };

  const handleCancel = () => {
    history.goBack();
  };

  const [deletePersonMfa, { loading: isLoadingDeleteMfa }] = useMutation(
    DeletePersonMfa,
    {
      variables: {
        immutableId: targetImmutableId,
        mfaType: type
      },
      onError: error => {
        onError(error);
        handleError(error);
      },
      onCompleted
    }
  );

  return (
    <Grid container justifyContent="center" style={{ padding: '40px 20px' }}>
      <Helmet title={ts('page.deletePersonMfa.title')} />
      <Grid item xs={12} sm={10} md={8} lg={6}>
        <Paper elevation={3}>
          <Box p={4}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Typography variant="h4">
                {ts('page.deletePersonMfa.title')}
              </Typography>
            </Box>
            <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-line' }}>
              {ts('page.deletePersonMfa.description')}
            </Typography>
            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                color="inherit"
                onClick={handleCancel}
                disabled={isLoadingDeleteMfa}
              >
                {ts('common.action.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (isLoadingDeleteMfa) {
                    return;
                  }
                  deletePersonMfa();
                }}
                disabled={isLoadingDeleteMfa}
                data-testid="delete-mfa-confirm"
              >
                {ts('common.action.delete')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

function PageDeletePersonMfa() {
  const { immutableId, targetedMfa } = useParams<{ accountCode: string; immutableId: string, targetedMfa: string }>();

  return (
    <CurrentPerson.Consumer>
      {() => (
        <ErrorHandler.Consumer>
          {errorHandler => (
            <DeletePersonMfaContent
              targetImmutableId={immutableId}
              type={targetedMfa as MfaType}
              onError={errorHandler.onError}
            />
          )}
        </ErrorHandler.Consumer>
      )}
    </CurrentPerson.Consumer>
  );
}

export default PageDeletePersonMfa;
